import React, { useState } from 'react';

import {
  QBox,
  QButton,
  QCloseButton,
  QDivider,
  QFlex,
  QHeading,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSpacer,
  QSpinner,
  QStep,
  QStepper,
  QStepperPanelActions,
  QStepperProgress,
  QSteps,
  QStepsActions,
  QText,
} from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { routes } from 'utils';
import { AddSupplierFormikPayload } from 'v2types';

import { ApprovalForm, GeneralInfoForm, RiskAssessmentForm, SupportingDocumentsForm } from './components';
import { useStepper } from './hooks';

type AddSupplierPresentationalProps = { formikValues: AddSupplierFormikPayload; isSupplierLoading: boolean };

export const AddSupplierPresentational: React.FC<AddSupplierPresentationalProps> = ({
  formikValues,
  isSupplierLoading,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isNameMissingModal, setIsNameMissingModal] = useState(false);
  const [isDraftButtonClicked, setIsDraftButtonClicked] = useState(false);

  const {
    handleNextStep,
    handlePreviousStep,
    handleStepChange,
    activeStep,
    steps,
    formik,
    handleCancelForm,
    handleSubmitForm,
    isSupplierCreating,
  } = useStepper(formikValues);

  const handleSaveDraftButton = (status: boolean) => {
    if (status) setIsDraftButtonClicked(true);
    else setIsDraftButtonClicked(false);
  };

  const saveAsADraftSupplier = () => {
    formik.setStatus(false);
    handleSaveDraftButton(true);
    if (formik.errors.contactEmail || formik.errors.name || formik.errors.intendedUse || formik.errors.contactNotes) {
      formik.setTouched({ ...formik.touched, contactEmail: true, name: true, contactNotes: true, intendedUse: true });
      handleStepChange(0); // Always show warnings on General Info page
    } else {
      if (!formik.values.name) {
        setIsNameMissingModal(true);
      } else {
        handleCancelForm();
      }
    }
  };

  const confirmSetIsNameMissingModalAction = () => {
    setIsNameMissingModal(false);
    handleStepChange(0);
  };

  return (
    <QBox minHeight="400px" data-cy="supplier-main-container">
      <QFlex align="center" mb={2}>
        <QHeading size={'lg'}>{t('addSupplier.stepper.title')}</QHeading>
        <QSpacer />
        <QCloseButton onClick={() => setIsCancelModalOpen(true)} />
      </QFlex>
      <QDivider />
      {isSupplierLoading ? (
        <QSpinner />
      ) : (
        <QBox mt={4}>
          <QStepper activeStep={activeStep} onStepItemClicked={handleStepChange}>
            <QSteps>
              <QStepperProgress />
              <QStep
                title={t('addSupplier.stepper.steps.generalInfo.title')}
                description={t('addSupplier.stepper.steps.generalInfo.description')}
                variant={steps[0].variant}
                data-cy="step-general-information"
              >
                <GeneralInfoForm formik={formik} isDraft={isDraftButtonClicked} handleStepChange={handleStepChange} />
              </QStep>
              <QStep
                title={t('addSupplier.stepper.steps.riskAssessment.title')}
                description={t('addSupplier.stepper.steps.riskAssessment.description')}
                variant={steps[1].variant}
                data-cy="step-risk-assessment"
              >
                <RiskAssessmentForm formik={formik} />
              </QStep>
              <QStep
                title={t('addSupplier.stepper.steps.supportingDocuments.title')}
                description={t('addSupplier.stepper.steps.supportingDocuments.description')}
                variant={steps[2].variant}
                data-cy="step-supporting-documents"
              >
                <SupportingDocumentsForm formik={formik} />
              </QStep>
              <QStep
                title={t('addSupplier.stepper.steps.approval.title')}
                description={t('addSupplier.stepper.steps.approval.description')}
                variant={steps[3].variant}
                data-cy="step-approval"
              >
                <ApprovalForm formik={formik} handleStepChange={handleStepChange} />
              </QStep>
              <QStepsActions>
                <QBox width="100%" sx={{ '& button': { whiteSpace: 'normal', height: 'auto', py: 2 } }}>
                  <QButton
                    data-cy="supplier-save-and-exit-btn"
                    width="100%"
                    variant="outline"
                    onClick={saveAsADraftSupplier}
                    isLoading={isSupplierCreating}
                  >
                    {t('addSupplier.stepper.buttons.saveAsDraft')}
                  </QButton>
                </QBox>
              </QStepsActions>
            </QSteps>
            <QStepperPanelActions>
              <QButton variant="ghost" onClick={() => setIsCancelModalOpen(true)}>
                {t('addSupplier.stepper.buttons.cancel')}
              </QButton>
              <QButton
                leftIcon="ArrowLeft"
                variant="outline"
                onClick={handlePreviousStep}
                isDisabled={activeStep === 0}
              >
                {t('addSupplier.stepper.buttons.previous')}
              </QButton>
              {activeStep === steps.length - 1 ? (
                <QButton onClick={handleSubmitForm} isLoading={isSupplierCreating}>
                  {t('addSupplier.stepper.buttons.sendForApproval')}
                </QButton>
              ) : (
                <QButton data-cy="supplier-next-btn" rightIcon="ArrowRight" onClick={handleNextStep}>
                  {t('addSupplier.stepper.buttons.next')}
                </QButton>
              )}
            </QStepperPanelActions>
          </QStepper>
        </QBox>
      )}
      <QModal isOpen={isCancelModalOpen} onClose={() => setIsCancelModalOpen(false)}>
        <QModalHeader>
          <QText>{t('addSupplier.modals.discardChanges.title')}</QText>
          <QCloseButton onClick={() => setIsCancelModalOpen(false)} />
        </QModalHeader>
        <QModalBody>
          <QText>{t('addSupplier.modals.discardChanges.message')}</QText>
        </QModalBody>
        <QModalActions>
          <QButton onClick={() => setIsCancelModalOpen(false)} variant="ghost">
            {t('addSupplier.modals.discardChanges.buttons.cancel')}
          </QButton>
          <QBox sx={{ '& button': { whiteSpace: 'normal', height: 'auto', py: 2 } }}>
            <QButton onClick={saveAsADraftSupplier} isLoading={isSupplierCreating} variant="outline">
              {t('addSupplier.modals.discardChanges.buttons.saveAsDraft')}
            </QButton>
          </QBox>
          <QBox sx={{ '& button': { whiteSpace: 'normal', height: 'auto', py: 2 } }}>
            <QButton isDestructive onClick={() => navigate(`/${routes.supplierList}`)}>
              {t('addSupplier.modals.discardChanges.buttons.discardChanges')}
            </QButton>
          </QBox>
        </QModalActions>
      </QModal>
      <QModal isOpen={isNameMissingModal} onClose={() => setIsNameMissingModal(false)}>
        <QModalHeader>
          <QText>{t('addSupplier.modals.cantSaveSupplier.title')}</QText>
        </QModalHeader>
        <QModalBody>
          <QText>{t('addSupplier.modals.cantSaveSupplier.message')}</QText>
        </QModalBody>
        <QModalActions>
          <QButton onClick={confirmSetIsNameMissingModalAction}>
            {t('addSupplier.modals.cantSaveSupplier.button')}
          </QButton>
        </QModalActions>
      </QModal>
    </QBox>
  );
};
